<template>
  <el-button :type="mergedProps.type" :size="buttonSize" :plain="mergedProps.plain" :round="mergedProps.round"
             :circle="circle" :loading="loading" :disabled="disabled" :icon="iconComponent" :color="mergedProps.color"
             :dark="dark" :link="link" :text="mergedProps.text" :bg="mergedProps.bg" :autofocus="autofocus"
             :native-type="nativeType" :auto-insert-space="autoInsertSpace" :loading-icon="loadingIcon" :tag="tag"
             v-bind="$attrs" @click="$emit('click')">
    <template v-if="!circle">
      <component :is="iconComponent" v-if="iconComponent && iconPosition === 'left' && !loading"
                 class="button-icon-left" />
      <span v-if="$slots.default" class="button-text">
        <slot></slot>
      </span>
      <component :is="iconComponent" v-if="iconComponent && iconPosition === 'right' && !loading"
                 class="button-icon-right" />
    </template>
  </el-button>
</template>

<script setup>
import { computed } from 'vue'
import {
  Edit, Delete, Search, Plus, Download, Upload, Refresh, Setting, View, Check, Close,
  Warning, InfoFilled, Star, Lock, Unlock, User, Phone, Message, Location, Share,
  Printer, Back, Right, Top, Bottom, Postcard, CopyDocument, ChatDotRound
} from '@element-plus/icons-vue'

// props
const props = defineProps({
  action: String, // 统一按钮标识
  icon: String,
  iconPosition: { type: String, default: 'left' },
  type: { type: String },
  size: { type: String, default: 'default' },
  color: String,
  plain: Boolean,
  round: Boolean,
  circle: Boolean,
  loading: Boolean,
  disabled: Boolean,
  dark: Boolean,
  link: Boolean,
  text: Boolean,
  bg: Boolean,
  autofocus: Boolean,
  nativeType: { type: String, default: 'button' },
  autoInsertSpace: { type: Boolean, default: undefined },
  loadingIcon: [String, Object],
  tag: { type: [String, Object], default: 'button' }
})

defineEmits([
  'click', 'change', 'focus', 'blur',
  'mouseenter', 'mouseleave', 'mousedown',
  'mouseup', 'keydown', 'keyup'
])

// 图标映射表
const iconMap = {
  edit: Edit,
  delete: Delete,
  search: Search,
  plus: Plus,
  add: Plus,
  download: Download,
  upload: Upload,
  refresh: Refresh,
  setting: Setting,
  view: View,
  check: Check,
  close: Close,
  warning: Warning,
  infoFilled: InfoFilled,
  star: Star,
  lock: Lock,
  unlock: Unlock,
  user: User,
  phone: Phone,
  message: Message,
  location: Location,
  share: Share,
  printer: Printer,
  back: Back,
  right: Right,
  top: Top,
  bottom: Bottom,
  postcard: Postcard,
  copyDocument: CopyDocument,
  chatDotRound: ChatDotRound,
}

// 按钮行为映射表（统一风格配置）
const actionMap = {
  查询: { icon: 'search', type: 'primary', plain: true },
  新增: { icon: 'plus', type: 'success' },
  编辑: { icon: 'edit', type: 'primary' },
  删除: { icon: 'delete', type: 'danger', plain: true },
  配置: { icon: 'setting', type: 'warning' },
  客户端: { icon: 'user', type: 'info' },
  生成: { icon: 'refresh', type: 'primary' },
  海报: { icon: 'view', type: 'info' },
  导出: { icon: 'download', type: 'success', plain: true },
  导入: { icon: 'upload', type: 'success' },
  打印: { icon: 'printer', type: 'info', plain: true },
  重打: { icon: 'printer', type: 'warning', plain: true },
  保存: { icon: 'check', type: 'primary' },
  上传: { icon: 'upload', type: 'primary' },
  清空: { icon: 'delete', type: 'danger', plain: true },
  更新: { icon: 'refresh', type: 'success' },
  病人档案: { icon: 'user', type: 'info' },
  建卡充值: { icon: 'postcard', type: 'warning' },
  挂号: { icon: 'check', type: 'primary' },
  取消: { icon: 'close', type: 'danger', plain: true },
  医保: { icon: 'infoFilled', type: 'primary' },
  回访: { icon: 'phone', type: 'info' },
  更多: { icon: 'right', type: 'info', text: true },
  CA签名用户: { icon: 'lock', type: 'warning' },
  复制: { icon: 'copyDocument', type: 'primary' },
  结算: { icon: 'check', type: 'warning' },
  预览: { icon: 'view', type: 'info' },
  返回: { icon: 'back', type: 'info', plain: true },
  关闭: { icon: 'close', type: 'default', plain: true },
  重置: { icon: 'refresh', type: 'warning' },
  分享: { icon: 'share', type: 'primary' },
  消息: { icon: 'message', type: 'primary' },
  微信: { icon: 'chatDotRound', type: 'success' },
}

// 统一优先使用 actionMap，如果未命中则回退自定义 icon/type
const actionConfig = computed(() => actionMap[props.action] || {})

const mergedProps = computed(() => ({
  icon: props.icon || actionConfig.value.icon,
  type: props.type || actionConfig.value.type || 'primary',
  plain: props.plain ?? actionConfig.value.plain ?? false,
  text: props.text ?? actionConfig.value.text ?? false,
  round: props.round ?? actionConfig.value.round ?? false,
  bg: props.bg ?? actionConfig.value.bg ?? false,
  color: props.color ?? actionConfig.value.color ?? props.color ?? undefined,
}))

const iconComponent = computed(() => {
  const iconKey = mergedProps.value.icon
  return iconMap[iconKey] || null
})

const buttonType = computed(() => {
  return props.type === 'primary' && props.action
      ? actionConfig.value.type || 'primary'
      : props.type
})

const buttonSize = computed(() => props.size)
</script>

<style scoped>
.button-text {
  display: inline-block;
}
</style>